import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DeleteBlogModal from './DeleteBlogModal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function BlogList() {

  const [postList, setPostList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  const usertype = localStorage.getItem('usertype')?.toLowerCase();

  const callApiPostContent = async () => {
    setLoading(true);
    try {
      const headers = {
        'x-access-token': localStorage.getItem('jwttoken')
      };
      const url = process.env.REACT_APP_API_URL + `blog/adminpaginated?page=${currentPage}&limit=${limit}&search=${searchTerm}`;
      const response = await axios.get(url, { headers });
      setPostList(response.data.data);
      setFilteredList(response.data.data);
      setTotalCount(response.data.totalCount);
      setTotalPages(response.data.totalPages);
    }
    catch (error) {
      console.log(error);
      setPostList([]);
      setFilteredList([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      callApiPostContent();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleOpenDeleteModal = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);
  };

  const handleDeletePost = async (postToDeleteSrno) => {
    try {
      const headers = {
        'x-access-token': localStorage.getItem('jwttoken')
      };
      const url = process.env.REACT_APP_API_URL + `posts/delete/${postToDeleteSrno}`;
      const response = await axios.delete(url, { headers });

      if (response.status === 200 || response.status === 204) {
        toast.success("Post permanently deleted!", { autoClose: 2000 });
        setShowDeleteModal(false);

        // FIX: Update state locally so the list clears instantly without refresh
        //const updatedList = postList.filter((item) => item.srno !== postToDeleteSrno);
        //setPostList(updatedList);

        // Optional: Re-fetch just to sync with DB
        callApiPostContent();
      } else {
        throw new Error("Server responded but did not delete.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete post.", { autoClose: 2000 });
      setShowDeleteModal(false);
    }
  };

  const activatePost = async (postToDeleteSrno) => {
    try {
      const headers = {
        'x-access-token': localStorage.getItem('jwttoken')
      };
      const url = process.env.REACT_APP_API_URL + `posts/activate/${postToDeleteSrno}`;
      await axios.put(url, {}, { headers });
      callApiPostContent();
    } catch (error) {
      console.error(error);
    }
  };

  const deactivatePost = async (postToDeleteSrno) => {
    try {
      const headers = {
        'x-access-token': localStorage.getItem('jwttoken')
      };
      const url = process.env.REACT_APP_API_URL + `posts/deactivate/${postToDeleteSrno}`;
      await axios.put(url, {}, { headers });
      callApiPostContent();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <ToastContainer />

      {/* Main Card Container */}
      <div className="main-card">

        {/* Toolbar Section */}
        <div className="toolbar-container">
          <h2 className="page-title">Blog Management</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="results-count">{totalCount} results</span>
            <input
              type="text"
              className="search-box"
              placeholder="Search by Title"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Link to="/blogcreate" className="btn-add-primary">
              + Create New Post
            </Link>
          </div>
        </div>

        {/* Gradient Header */}
        <div className="table-header-panel">
          Post List
        </div>

        {/* Table */}
        <div>
          <table className="custom-table" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>SR No.</th>
                <th>Post ID</th>
                <th>Title</th>
                <th>Heading</th>
                <th>Category</th>
                <th>Author</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8">
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="empty-state">
                      <div className="empty-state-icon">📝</div>
                      <div className="empty-state-text">No posts found</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredList.map((item, index) => (
                  <tr key={item.srno || index}>
                    <td>{item.srno}</td>
                    <td>{item.postid}</td>
                    <td style={{ fontWeight: '600' }}>{item.posttitle}</td>
                    <td>{item.postheading}</td>
                    <td>{item.category}</td>
                    <td>{item.author}</td>
                    <td>
                      <span className={item.isactive === 1 ? 'badge-active' : 'badge-inactive'}>
                        {item.isactive === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-btn-container">
                        <Link to={`/blogupdate/${item.srno}`} className="btn-action-edit">
                          Edit
                        </Link>
                        <Link to={`/blogview/${item.srno}`} className="btn-action-edit">
                          View
                        </Link>
                        {(usertype === 'admin') && (
                          <>
                            <button
                              onClick={() => handleOpenDeleteModal(item)}
                              className="btn-action-delete"
                            >
                              Delete
                            </button>
                            <button
                              onClick={() => item.isactive === 1 ? deactivatePost(item.srno) : activatePost(item.srno)}
                              className={item.isactive === 1 ? 'btn-action-delete' : 'btn-action-edit'}
                            >
                              {item.isactive === 1 ? 'Deactivate' : 'Activate'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center mt-4 mb-2 gap-3 pb-3">
            <button
              className="btn btn-outline-primary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span className="fw-bold">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-outline-primary"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}

      </div>

      {showDeleteModal && (
        <DeleteBlogModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => handleDeletePost(postToDelete.srno)}
          postToDelete={postToDelete}
        />
      )}
    </div>
  );
}

export default BlogList;